<script>
    import {_} from 'svelte-i18n';
    import Fa from 'svelte-fa';
    import {faCircleCheck, faXmark} from '@fortawesome/free-solid-svg-icons';
    import {onMount} from "svelte";
    import {get} from 'svelte/store';
    import {getAnalytics, logEvent} from 'firebase/analytics';
    import {logMetric} from "../metrics";
    import ItemsListing from "./ItemsListing.svelte";
    import {claim_gift_route} from "../constants";
    import {tagNetworkFailure, tagBodyReadFailure} from "./network-failure.js";
    import {resourceDetails} from "../globals.js";
    import {storePacks, user, navigateToSection, scrollToPackId, storeSection, subscriptions, endExpiredSession, isSessionExpired} from "../stores.js";
    import {goTo, isPrimetimePendingStart} from "../utils.js";

    let { _systemMessages, system_message_modal_hide, globals = {} } = $props();

    function getLowestFeaturedPack(packs) {
        const featuredPacks = packs.filter(p => {
            if (!p.isFeatured) return false;
            if (isPrimetimePendingStart(p)) return false;
            return p.storeSection === 'promos' ||
                (p.storeSection === 'goldPacks' && p.specialTitle?.toLowerCase().includes('primetime'));
        });
        if (featuredPacks.length === 0) return null;
        return featuredPacks.sort((a, b) => {
            const positionDiff = (a.position || 0) - (b.position || 0);
            if (positionDiff !== 0) return positionDiff;
            if (a.showPlayerName && !b.showPlayerName) return -1;
            if (!a.showPlayerName && b.showPlayerName) return 1;
            return 0;
        })[0];
    }

    function resolvePackName(pack) {
        if (!pack) return null;
        if (pack.titleNorthKey && pack.titleSouthKey) {
            return $_($user.user.side === 1 ? pack.titleNorthKey : pack.titleSouthKey);
        }
        if (pack.specialTitle) {
            return $_(pack.specialTitle);
        }
        return null;
    }
    const analytics = getAnalytics();

    function formatTimeFriendly(seconds) {
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        let days = Math.floor(hours / 24);

        let timeString = `${minutes} ${$_('Minute')}`;

        if (seconds - 1 > 60 * 60 * 24) {
            timeString = `${days} ${$_('Days')}`;
        } else if (seconds > 60 * 60) {
            timeString = `${hours} ${$_('Hours')}`;
        } else if (seconds >= 60) {
            timeString = `${minutes} ${$_('Minutes')}`;
        }

        return timeString;
    }

    // svelte-ignore state_referenced_locally
    globals.resourceDetails = resourceDetails;
    // svelte-ignore state_referenced_locally
    globals.formatTimeFriendly = formatTimeFriendly;

    let message = $state(null);
    let processing = $state(false);
    let loaded = $state(false);
    let claimed = $state(false);
    let isPresetSystemMessage = $state(false);
    let packSection = $state(null);
    let packId = $state(null);

    onMount(async () => {
        message = Object.values(_systemMessages)[0];

        const packIdMatch = message.link?.match(/^Pack id:(\d+)$/);
        if (packIdMatch) {
            packId = parseInt(packIdMatch[1]);
            const freeWebshopPacks = ['wssubdays7pack1', 'wssubdays7pack5', 'wssubdays14pack10', 'stpatrickwebshop20', 'stpatrickwebshop300', 'newplayergiftsub1', 'returnpack5'];

            // Search storePacks first, then fall back to subscriptions
            let pack = get(storePacks).find(p => p.internalId === packId);
            let sub = null;
            if (!pack) {
                sub = get(subscriptions).find(s => s.packId === packId);
            }

            const packName = pack ? resolvePackName(pack)
                : sub ? $_(sub.bundleName + 'Title')
                : null;
            if (packName) {
                message = {
                    ...message,
                    subject: message.subject?.replace('{PACK_NAME}', packName),
                    body: message.body?.replace('{PACK_NAME}', packName),
                };
            }
            isPresetSystemMessage = true;

            if (pack) {
                const lowestFeatured = getLowestFeaturedPack(get(storePacks));
                packSection = (lowestFeatured && lowestFeatured.internalId === packId)
                    ? 'featured'
                    : pack.storeSection;
            } else if (sub) {
                // Free packs show in promos, regular subscriptions in goldAndResources
                packSection = freeWebshopPacks.includes(sub.bundleName)
                    ? 'promos'
                    : 'subscriptions';
            }

            const seenId = localStorage.getItem('presetSystemMessageId');
            if (seenId === String(message.id)) {
                system_message_modal_hide();
                return;
            }
            localStorage.setItem('presetSystemMessageId', String(message.id));
        }

        await logMetric({show_system_message: "true"});

        logEvent(analytics, 'screen_view', {
            firebase_screen: 'system_message_modal',
            firebase_screen_class: 'web',
        });
        loaded = true;
    });

    async function claimGift() {
        try {
            processing = true;
            const response = await fetch(claim_gift_route, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    messageId: message.id,
                }),
            }).catch(tagNetworkFailure);

            // web-shop's 403 is the LoggedInGuard: the session is gone, so swap the shop for <Login/>
            // as every other API caller does (Rollbar #2519) instead of reporting it (#360).
            if (isSessionExpired(response)) {
                endExpiredSession(claim_gift_route);
                return;
            }

            if (!response.ok) {
                const message = `An error has occured: ${response.status}`;
                throw new Error(message);
            }

            const res = await response.text().catch(tagBodyReadFailure);
            claimed = true;
            // console.log(res)

        } catch (error) {
            // A dropped connection is not a fault of ours (Rollbar #365).
            if (error?.isNetworkFailure) {
                Rollbar.info('Claim gift network failure: ' + error);
            } else {
                Rollbar.error("Error claiming gift " + error)
            }
        } finally {
            processing = false;
        }
    }

    const subTabSections = {
        goldPacks: { section: 'goldAndResources', tab: 'gold' },
        subscriptions: { section: 'goldAndResources', tab: 'subscriptions' },
        resources: { section: 'goldAndResources', tab: 'resources' },
    };

    function checkItOut() {
        system_message_modal_hide();
        if (packId) scrollToPackId.set(packId);
        if (!packSection) return;

        const subTab = subTabSections[packSection];
        if (subTab) {
            storeSection.set(subTab.section);
            goTo(`/supplies/${subTab.section}?tab=${subTab.tab}`, true);
        } else {
            navigateToSection(packSection);
        }
    }
</script>
{#if loaded}
    <div class="card system-message animate__animated animate__bounceInDown">
        <!-- svelte-ignore a11y_invalid_attribute -->
        <a class="close-button" href="javascript:" onclick={system_message_modal_hide}>
            <Fa fw icon={faXmark} primaryColor="#240c05" size="2x"/>
        </a>
        <h1>{message.subject}</h1>
        {#if isPresetSystemMessage}
            <h4>{@html message.body}</h4>
        {:else}
            <h4>{message.body}</h4>
        {/if}
        {#if !isPresetSystemMessage}
            <ItemsListing storePack={{}} goldAmount={0} globals={globals} items={message.items} hasMask={false}/>
        {/if}
        {#if claimed}
            <Fa icon={faCircleCheck} fw size="3x" class="claimed-mark animate__animated animate__heartBeat"
                primaryColor="green"/>
        {:else if isPresetSystemMessage}
            <button class="button primary claim-button green" onclick={checkItOut}>
                {$_('CrossPromoButton')}
            </button>
        {:else}
            <button
                    class="button primary claim-button green"
                    class:loading={processing}
                    onclick={claimGift}
            >
                {$_('Claim')}
            </button>
        {/if}
    </div>
{/if}

<style lang="scss">
  :global {
    .system-message {
      &.modal {
        min-width: 450px !important;
      }

      @media (max-width: 700px) {
        &.modal {
          min-width: 350px !important;
        }
      }

      &.card {
        align-items: center;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: center;
        text-align: center;
        overflow: hidden;
        text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
        transition: all 0.2s ease-in;
        position: relative;


        h1 {
          font-weight: 700;
          font-family: var(--font-family-sans-condensed);
          padding: 1rem 2rem;
          line-height: 1.2em;
          margin-bottom: 0;
          margin-top: 0;
        }

        h4 {
          font-family: var(--font-family-sans-condensed);
          max-width: 400px;
          font-size: 16px;
          color: #8c7138;
          margin: 10px auto;
        }

        .claim-button, .claimed-mark {
          margin: 20px 0;
        }

        .items {
          background-color: rgba(255, 255, 255, 0.5);
          border-radius: 6px;
          padding: 10px;
          margin: 0;
          width: auto;
        }

        .close-button {
          position: absolute;
          top: 0;
          right: 0;
          padding: 7px 2px;
        }
      }

    }
  }
</style>